import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Popconfirm, Space, Tooltip } from 'antd';
import { Button, Status } from '@insight-health-ai/roseate/components';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { getApiErrorMessage, magicFlowApi } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import type { MagicFlowOption, Recipe } from 'src/models';

type UseMagicFlowRecipesControllerParams = {
  scopedHeaders: Record<string, string | boolean>;
  recipeHeaders: Record<string, string | boolean>;
};

type RecipeListResponse = {
  recipes: Recipe[];
  page?: number;
  page_size?: number;
  total?: number;
};

export const useMagicFlowRecipesController = ({
  recipeHeaders,
}: UseMagicFlowRecipesControllerParams) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: recipes = [],
    isLoading: isLoadingRecipes,
    isFetching: isFetchingRecipes,
  } = useQuery({
    queryKey: ['magicflow-recipes-global'],
    enabled: true,
    queryFn: () =>
      magicFlowApi
        .get<RecipeListResponse>('/recipe', {
          headers: recipeHeaders,
          params: {
            page: 1,
            page_size: 200,
            active_only: false,
          },
        })
        .then((res) => res.data.recipes || []),
  });

  const deleteRecipeMutation = useMutation({
    mutationFn: (recipeId: string) =>
      magicFlowApi.delete(`/recipe/${recipeId}`, {
        headers: recipeHeaders,
      }),
    onSuccess: () => {
      showToastSuccess('Recipe deleted successfully');
      queryClient.invalidateQueries({
        queryKey: ['magicflow-recipes-global'],
      });
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) || 'Failed to delete recipe'
      );
    },
  });

  const recipeTableData = useMemo(
    () =>
      recipes.map((recipe) => ({
        ...recipe,
        key: recipe.recipe_id,
      })),
    [recipes]
  );

  const recipeColumns: ColumnsType<Recipe & { key: string }> = [
    {
      title: 'Name',
      dataIndex: 'name',
      width: 240,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: 'Version',
      dataIndex: 'version',
      width: 90,
      render: (value, record) => (
        <Space>
          <span className="font-mono">v{value ?? '-'}</span>
          {record.is_active ? (
            <Status appearance="success" label="active" size="xs" />
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      width: 200,
      render: (value) =>
        value ? dayjs(value).format('MMM D, YYYY h:mm A') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space size={8}>
          <Tooltip title="Edit">
            <span>
              <Button
                variant="outlined"
                appearance="secondary"
                size="sm"
                icon={<EditOutlined />}
                onClick={() =>
                  navigate(`/superadmin/magicflow/recipes/${record.recipe_id}`)
                }
              />
            </span>
          </Tooltip>
          <Popconfirm
            title="Delete recipe"
            description="This recipe will be soft deleted."
            onConfirm={() => deleteRecipeMutation.mutate(record.recipe_id)}
            okButtonProps={{ loading: deleteRecipeMutation.isPending }}
          >
            <span>
              <Button
                variant="outlined"
                appearance="danger"
                size="sm"
                icon={<DeleteOutlined />}
              />
            </span>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const recipeOptions: MagicFlowOption[] = useMemo(
    () =>
      recipes
        .filter((recipe) => recipe.is_active !== false)
        .map((recipe) => ({
          value: recipe.recipe_id,
          label: `${recipe.name} (v${recipe.version ?? '?'})`,
        })),
    [recipes]
  );

  return {
    recipes,
    recipeTableData,
    recipeColumns,
    isLoadingRecipes,
    isFetchingRecipes,
    recipeOptions,
    openCreateRecipe: () => navigate('/superadmin/magicflow/recipes/new'),
  };
};
